import React from 'react';
import { Dialog, DialogHeader, DialogBody, DialogFooter, Button } from '@repo/ui';

interface ColumnVisibilityModalProps {
  isOpen: boolean;
  onClose: () => void;
  visibleColumns: Record<string, boolean>;
  onToggleColumn: (key: string) => void;
  onToggleAllColumns: (checked: boolean) => void;
}

const ColumnVisibilityModal = React.memo<ColumnVisibilityModalProps>(
  ({ isOpen, onClose, visibleColumns, onToggleColumn, onToggleAllColumns }) => {
    const scrollContainerRef = React.useRef<HTMLDivElement>(null);
    const savedScrollPositionRef = React.useRef<number | null>(null);
    const isUserInteractionRef = React.useRef<boolean>(false);

    // Restore scroll position when visibleColumns changes (only after user interaction)
    React.useLayoutEffect(() => {
      if (
        isUserInteractionRef.current &&
        savedScrollPositionRef.current !== null &&
        scrollContainerRef.current
      ) {
        // Restore scroll position synchronously before browser paint
        const savedPosition = savedScrollPositionRef.current;
        scrollContainerRef.current.scrollTop = savedPosition;
        savedScrollPositionRef.current = null;
        isUserInteractionRef.current = false;

        // Double-check after a microtask to ensure it stuck
        Promise.resolve().then(() => {
          if (
            scrollContainerRef.current &&
            scrollContainerRef.current.scrollTop !== savedPosition
          ) {
            scrollContainerRef.current.scrollTop = savedPosition;
          }
        });
      }
    }, [visibleColumns]);

    const handleColumnToggle = React.useCallback(
      (key: string, event: React.MouseEvent<HTMLButtonElement>) => {
        if (scrollContainerRef.current) {
          // Capture scroll position before state update
          savedScrollPositionRef.current = scrollContainerRef.current.scrollTop;
          isUserInteractionRef.current = true;

          // Blur the button to prevent focus-related scrolling
          event.currentTarget.blur();

          onToggleColumn(key);
        }
      },
      [onToggleColumn]
    );

    const handleToggleAllColumns = React.useCallback(
      (checked: boolean, event: React.MouseEvent<HTMLButtonElement>) => {
        if (scrollContainerRef.current) {
          // Capture scroll position before state update
          savedScrollPositionRef.current = scrollContainerRef.current.scrollTop;
          isUserInteractionRef.current = true;

          // Blur the button to prevent focus-related scrolling
          event.currentTarget.blur();

          onToggleAllColumns(checked);
        }
      },
      [onToggleAllColumns]
    );

    const getVisibleColumnCount = React.useCallback(() => {
      return Object.values(visibleColumns).filter(Boolean).length;
    }, [visibleColumns]);

    const getTotalColumnCount = React.useCallback(() => {
      return Object.keys(visibleColumns).length;
    }, [visibleColumns]);

    const columnGroups = React.useMemo(
      () => [
        {
          title: 'Basic Information',
          columns: [
            { key: 'client', label: 'Client Name', required: false },
            { key: 'createdAt', label: 'Created At', required: false },
            { key: 'createdBy', label: 'Created By', required: false },
            { key: 'paymentLink', label: 'Payment Link', required: false },
          ],
        },
        {
          title: 'Trust Request Bill',
          columns: [
            { key: 'trustRequestEmailType', label: 'Email Type', required: false },
            { key: 'issueAt', label: 'Issue Date', required: false },
            { key: 'dueAt', label: 'Due Date', required: false },
            { key: 'total', label: 'Total Amount', required: false },
            { key: 'paid', label: 'Paid Amount', required: false },
            { key: 'pending', label: 'Pending Amount', required: false },
          ],
        },
      ],
      []
    );

    return (
      <Dialog open={isOpen} onClose={onClose} size="lg" zIndex="z-[300]">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-semibold text-xtnd-dark dark:text-xtnd-light">
                Edit Columns
              </h3>
              <p className="text-sm text-xtnd-dark-400 dark:text-xtnd-dark-300 mt-1">
                {getVisibleColumnCount()} of {getTotalColumnCount()} columns visible
              </p>
            </div>
          </div>
        </DialogHeader>

        <DialogBody ref={scrollContainerRef} className="space-y-6 max-h-[60vh] overflow-y-auto">
          {/* Select All Toggle */}
          <div className="p-4 bg-xtnd-dark-50 dark:bg-xtnd-dark-700 rounded-lg border border-xtnd-dark-100 dark:border-xtnd-dark-600">
            <Button
              variant={getVisibleColumnCount() === getTotalColumnCount() ? 'primary' : 'outline'}
              size="sm"
              onClick={e =>
                handleToggleAllColumns(getVisibleColumnCount() !== getTotalColumnCount(), e)
              }
            >
              {getVisibleColumnCount() === getTotalColumnCount() ? 'Deselect All' : 'Select All'}
            </Button>
            <p className="text-sm text-xtnd-dark-500 dark:text-xtnd-dark-300 mt-2">
              Toggle all columns at once
            </p>
          </div>

          {/* Column Groups */}
          {columnGroups.map(group => (
            <div key={group.title} className="space-y-3">
              <h4 className="text-sm font-semibold text-xtnd-dark-600 dark:text-xtnd-dark-200 uppercase tracking-wider border-b border-xtnd-dark-100 dark:border-xtnd-dark-600 pb-2">
                {group.title}
              </h4>
              <div className="space-y-2">
                {group.columns.map(({ key, label, required }) => (
                  <div
                    key={key}
                    className="flex items-center justify-between p-3 hover:bg-xtnd-dark-50 dark:hover:bg-xtnd-dark-700 rounded-lg transition-colors"
                  >
                    <div className="flex items-center space-x-3 flex-1">
                      <Button
                        variant={visibleColumns[key] ? 'primary' : 'outline'}
                        size="sm"
                        onClick={e => {
                          if (!required) {
                            handleColumnToggle(key, e);
                          }
                        }}
                        disabled={required}
                        className="min-w-[80px]"
                      >
                        {visibleColumns[key] ? 'Hide' : 'Show'}
                      </Button>
                      <span
                        className={`text-sm flex-1 ${required ? 'text-xtnd-dark-400 dark:text-xtnd-dark-300' : 'text-xtnd-dark-800 dark:text-xtnd-dark-50'}`}
                      >
                        {label}
                      </span>
                    </div>
                    {required && (
                      <span className="text-xs font-medium text-xtnd-dark-300 dark:text-xtnd-dark-400 bg-xtnd-dark-50 dark:bg-xtnd-dark-700 px-2 py-1 rounded ml-3">
                        Required
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </DialogBody>

        <DialogFooter>
          <div className="flex items-center justify-between w-full">
            <div className="text-sm text-xtnd-dark-400 dark:text-xtnd-dark-300">
              Changes are saved automatically
            </div>
            <Button variant="primary" onClick={onClose}>
              Done
            </Button>
          </div>
        </DialogFooter>
      </Dialog>
    );
  }
);

ColumnVisibilityModal.displayName = 'ColumnVisibilityModal';

export default ColumnVisibilityModal;
